import { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

const PAGE_SIZE = 50;

const getProdutoLabel = (nomeProduto) => nomeProduto || "Usuarios e permissoes";

const getAuthToken = () => {
  const rawToken =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    "";

  return rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
};

const getNomeCampoCorreto = (nome) => {
  switch (nome) {
    case "Contexto_produto":
    case "Contextoproduto":
    case "contextoProduto":
      return "Informacoes do Produto";
    case "Contexto_institucional":
    case "Contextoinstitucional":
      return "Informacoes Institucionais";
    case "Contexto1":
      return "Informacoes Adicionais 1";
    case "Contexto2":
      return "Informacoes Adicionais 2";
    default:
      return nome || "-";
  }
};

const formatarDataHora = (valor) => {
  if (!valor) return "";
  if (typeof valor !== "string") return String(valor);

  if (/^\d{2}\/\d{2}\/\d{4}\s*-\s*\d{2}:\d{2}$/.test(valor)) {
    return valor;
  }

  const match = valor.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))/);
  if (match) {
    const [, yyyy, mm, dd, hh, min] = match;
    return `${dd}/${mm}/${yyyy} - ${hh}:${min}`;
  }

  return valor;
};

const mapHistoricoItem = (item, index) => ({
  id: `${item?.idClienteAipim ?? "cliente"}-${item?.idUsuario ?? "usuario"}-${item?.dataHoraCadastro ?? index}`,
  produto: getProdutoLabel(item?.nomeProduto),
  nome: getNomeCampoCorreto(item?.campo),
  alteradoPor: item?.nomeUsuario || "-",
  data: formatarDataHora(item?.dataHoraCadastro),
  dataOrdenacao: item?.dataHoraCadastro ? new Date(item.dataHoraCadastro).getTime() : 0,
  justificativa: item?.justificativa || "-",
  mudancas: {
    antes: item?.valorAnterior || "",
    depois: item?.valorNovo || "",
  },
});

const GovernancaAuditoria = () => {
  const [historicoData, setHistoricoData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [temProximaPagina, setTemProximaPagina] = useState(false);

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchHistorico = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const token = getAuthToken();
        if (!token) {
          setHistoricoData([]);
          setErrorMessage("Token de autenticacao nao encontrado.");
          return;
        }

        const decodedToken = jwtDecode(token);
        const idClienteAipim = decodedToken.id_cliente;

        if (!idClienteAipim) {
          setHistoricoData([]);
          setErrorMessage("Cliente nao identificado no token.");
          return;
        }

        const response = await axios.get(`${baseURL}/Justificativa/historico`, {
          params: {
            idClienteAipim,
            pagina,
            tamanho: PAGE_SIZE,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        const consolidated = (Array.isArray(response.data?.itens) ? response.data.itens : [])
          .map((item, index) => mapHistoricoItem(item, index))
          .sort((a, b) => b.dataOrdenacao - a.dataOrdenacao)
          .map((item, index) => ({ ...item, id: `${item.id}-${index}` }));

        setHistoricoData(consolidated);
        setTemProximaPagina(response.data?.temProximaPagina === true);
      } catch (error) {
        console.error("Erro ao buscar historico de auditoria:", error);
        setHistoricoData([]);
        setTemProximaPagina(false);
        setErrorMessage("Nao foi possivel carregar os registros de auditoria.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistorico();
  }, [baseURL, pagina]);

  const handleFecharModal = () => {
    setItemSelecionado(null);
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Auditoria
        </Typography>
        <Typography color="text.secondary">
          Registros consolidados de alteracoes em configuracoes operacionais.
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        })}
      >
        <Table sx={{ minWidth: 850 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 700 }}>Produto</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Informacao</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Quem alterou</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Data da alteracao</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Justificativa</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Alteracoes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 5 }}>
                    <CircularProgress size={40} />
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                      Carregando registros de auditoria...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : errorMessage ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ py: 3, color: "error.main" }}>{errorMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : historicoData.length > 0 ? (
              historicoData.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    <Chip label={item.produto} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.nome}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.alteradoPor}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.data}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 320 }}>
                      {item.justificativa}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => setItemSelecionado(item)}
                      size="small"
                      sx={{
                        color: "primary.main",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ py: 3, color: "text.secondary" }}>
                    Nenhum registro de auditoria disponivel
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && !errorMessage && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Pagina {pagina + 1}
          </Typography>
          <Button variant="outlined" size="small" disabled={pagina === 0} onClick={() => setPagina((current) => current - 1)}>
            Anterior
          </Button>
          <Button variant="contained" size="small" disabled={!temProximaPagina} onClick={() => setPagina((current) => current + 1)}>
            Proxima
          </Button>
        </Stack>
      )}

      <Dialog open={Boolean(itemSelecionado)} onClose={handleFecharModal} maxWidth="md" fullWidth>
        {itemSelecionado && (
          <>
            <DialogTitle
              sx={{
                backgroundColor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Detalhes da alteracao
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {itemSelecionado.produto} - {itemSelecionado.nome} - {itemSelecionado.data}
                </Typography>
              </Box>
              <IconButton onClick={handleFecharModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  backgroundColor: "background.default",
                  p: 2.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: "#d32f2f" }}>
                    Antes:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 2,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(244, 67, 54, 0.12)" : "#ffebee",
                      color: "text.primary",
                      borderRadius: 1,
                      fontFamily: "monospace",
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                      maxHeight: 400,
                      overflowY: "auto",
                      overflowX: "hidden",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {itemSelecionado.mudancas.antes}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: "#2e7d32" }}>
                    Depois:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 2,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(76, 175, 80, 0.12)" : "#e8f5e9",
                      color: "text.primary",
                      borderRadius: 1,
                      fontFamily: "monospace",
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                      maxHeight: 400,
                      overflowY: "auto",
                      overflowX: "hidden",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {itemSelecionado.mudancas.depois}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Stack>
  );
};

export default GovernancaAuditoria;
